import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';

const itemTypeOptions = [
  { value: 1, label: '💼 Service' },
  { value: 2, label: '🛍️ Retail' },
  { value: 3, label: '📦 Sundry' },
  { value: 5, label: '🎁 Gift' },
  { value: 6, label: '📚 Series' },
  { value: 7, label: '👑 Membership' },
  { value: 8, label: '🎉 Promotions' },
];

const CommissionFilters = ({ filters, setFilters }) => {
  const formatDate = (date) => date.toISOString().split('T')[0];

  // Always initialize itemType as an array
  const [draftFilters, setDraftFilters] = useState({
    ...filters,
    itemType: filters.itemType || [],
  });
  const [employees, setEmployees] = useState([]);

  /* ------------------------------------------------------------------ */
  /* Sync incoming filters → draft                                       */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    setDraftFilters({
      ...filters,
      itemType: filters.itemType || [],
    });
  }, [filters]);

  /* ------------------------------------------------------------------ */
  /* Load employees                                                      */
  /* ------------------------------------------------------------------ */
useEffect(() => {
  api
    .get('/commission/employee?mode=filter')
    .then(res => setEmployees(res.data))
    .catch(err => {
      console.error('Failed to load employees', err);
      setEmployees([]);
    });
}, []);


  /* ------------------------------------------------------------------ */
  /* Apply Filters (normalize itemType → array)                          */
  /* ------------------------------------------------------------------ */
  const applyFilters = () => {
    if (!draftFilters.from || !draftFilters.to) return;

    setFilters({
      ...draftFilters,
      itemType: Array.isArray(draftFilters.itemType)
        ? draftFilters.itemType
        : draftFilters.itemType
          ? [draftFilters.itemType]
          : [],
    });
  };

  /* ------------------------------------------------------------------ */
  /* Preset Dates                                                        */
  /* ------------------------------------------------------------------ */
  const setPreset = (type) => {
    const now = new Date();
    let from, to;

    switch (type) {
      case 'today':
        from = to = formatDate(now);
        break;
      case 'week': {
        const start = new Date(now);
        start.setDate(now.getDate() - 6);
        from = formatDate(start);
        to = formatDate(now);
        break;
      }
      case 'month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        from = formatDate(start);
        to = formatDate(now);
        break;
      }
      case 'year': {
        const start = new Date(now.getFullYear(), 0, 1);
        from = formatDate(start);
        to = formatDate(now);
        break;
      }
      default:
        return;
    }

    const nextDraft = { ...draftFilters, range: type, from, to };
    setDraftFilters(nextDraft);
    setFilters({
      ...nextDraft,
      itemType: nextDraft.itemType || [],
    });
  };

  /* ------------------------------------------------------------------ */
  /* Change Detection                                                    */
  /* ------------------------------------------------------------------ */
  const hasChanges =
    draftFilters.from !== filters.from ||
    draftFilters.to !== filters.to ||
    draftFilters.employeeId !== filters.employeeId ||
    JSON.stringify(draftFilters.itemType || []) !== JSON.stringify(filters.itemType || []);

  const presetOptions = [
    { value: 'today', label: 'Today', icon: '📅' },
    { value: 'week', label: 'Week', icon: '📆' },
    { value: 'month', label: 'Month', icon: '🗓️' },
    { value: 'year', label: 'Year', icon: '📊' },
  ];

  return (
    <div className="filters bg-white shadow-lg rounded-xl p-4 sm:p-6 mb-6">
      <div className="flex flex-col gap-4">

        {/* Header */}
        <div className="flex items-center gap-2">
          <div className="w-1 h-8 bg-gradient-to-b from-cyan-500 to-blue-500 rounded-full" />
          <h3 className="text-lg font-semibold text-slate-700">
            Commission Filters
          </h3>
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-2 bg-slate-50 p-1 rounded-lg">
          {presetOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setPreset(option.value)}
              className={`flex-1 min-w-[80px] px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                filters.range === option.value
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-md'
                  : 'text-slate-600 hover:bg-white hover:shadow-sm'
              }`}
            >
              <span className="mr-1">{option.icon}</span>
              {option.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">

          {/* From */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={draftFilters.from || ''}
              onChange={(e) =>
                setDraftFilters({ ...draftFilters, from: e.target.value, range: undefined })
              }
              className="w-full px-4 py-2.5 border rounded-lg"
            />
          </div>

          {/* To */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={draftFilters.to || ''}
              onChange={(e) =>
                setDraftFilters({ ...draftFilters, to: e.target.value, range: undefined })
              }
              className="w-full px-4 py-2.5 border rounded-lg"
            />
          </div>

          {/* Employee */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Employee
            </label>
            <select
              value={draftFilters.employeeId || ''}
              onChange={(e) =>
                setDraftFilters({
                  ...draftFilters,
                  employeeId: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              className="w-full px-4 py-2.5 border rounded-lg bg-white"
            >
              <option value="">All Employees</option>
              {employees.map(emp => (
                <option key={emp.value} value={emp.value}>
                  {emp.label}
                </option>
              ))}
            </select>
          </div>

          {/* Item Type */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Item Type
            </label>
            <select
              value={draftFilters.itemType[0] || ''} // ✅ safe access
              onChange={(e) =>
                setDraftFilters({
                  ...draftFilters,
                  itemType: e.target.value ? [Number(e.target.value)] : [],
                })
              }
              className="w-full px-4 py-2.5 border rounded-lg bg-white"
            >
              <option value="">All Types</option>
              {itemTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Apply */}
          <div className="flex items-end">
            <button
              onClick={applyFilters}
              disabled={!draftFilters.from || !draftFilters.to || !hasChanges}
              className="w-full px-4 py-2.5 rounded-lg font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-white disabled:opacity-40"
            >
              ✨ Apply Filters
            </button>
          </div>
        </div>

        {hasChanges && (
          <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
            ⚠️ You have unsaved filter changes.
          </div>
        )}

      </div>
    </div>
  );
};

export default CommissionFilters;
